import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';
import { v4 as uuidV4 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';

import {
  useChatStore,
  useDrawerStore,
  useNotificationStore,
  usePeersStore,
  useRoomStore,
  useRoomActionsStore,
} from '@src/stores';

import { useRoomContext, useTheme } from '@src/contexts';

import { getRoomId } from '@src/helpers';

import useSpeechMessage from './useSpeachMessage';

import { allowedTypes, blockedExtensions, RESTRICTION_CHAT } from './chatBox.constants';
import { EWebinarSockets } from '@src/constants';

import type { IMessage } from '@src/types';
import { ECommonSocketKeys, EMeetingType, EMessageOperations, EMessageType } from '@src/types';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

const useChatBox = () => {
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSpeechActive, setIsSpeechActive] = useState(false);
  const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  // existing attachment from the message being edited
  const [existingAttachment, setExistingAttachment] = useState<{
    fileName: string;
    fileType?: string;
    fileUrl?: string;
  } | null>(null);
  // flag when user deletes the existing attachment while editing
  const [removeExistingAttachment, setRemoveExistingAttachment] = useState(false);

  const chatCache = useRef<Map<string, IMessage[]>>(new Map());

  const { theme } = useTheme();

  const { isJoinedRoom, meetingType } = useRoomStore(
    useShallow((state) => ({ isJoinedRoom: state.isJoinedRoom, meetingType: state.meetingType }))
  );

  const {
    appendMessage,
    deleteMessage,
    publicMessages,
    privateMessages,
    panelistMessages,
    selectedReceiverData,
    setSelectedReceiverData,
    updateMessage,
    typingStatus,

    // EDITING state from store
    editingMessageId,
    setEditingMessageId,
    editingInitialText,
    setEditingInitialText,
  } = useChatStore(
    useShallow((state) => ({
      appendMessage: state.appendMessage,
      deleteMessage: state.deleteMessage,
      updateMessage: state.updateMessage,
      publicMessages: state.publicMessages,
      privateMessages: state.privateMessages,
      panelistMessages: state.panelistMessages,
      selectedReceiverData: state.selectedReceiverData,
      setSelectedReceiverData: state.setSelectedReceiverData,
      typingStatus: state.typingStatus,

      editingMessageId: state.editingMessageId,
      setEditingMessageId: state.setEditingMessageId,
      editingInitialText: state.editingInitialText,
      setEditingInitialText: state.setEditingInitialText,
    }))
  );

  // Check if the message can be saved (new or edit)
  const canSaveEdit = useMemo(() => {
    const isEditingNow = Boolean(editingMessageId);
    const trimmed = message.trim();

    //For new (non-edit) messages, the Send/Save is enabled when either:
    //trimmed has at least one character or selectedFile exists
    if (!isEditingNow) {
      return trimmed.length > 0 || !!selectedFile;
    }

    // Editing: disable save when no text or attachment changes or when the message is empty and no attachment remains
    const originalText = (editingInitialText || '').trim();
    const isTextChanged = trimmed !== originalText;
    const hadAttachment = !!existingAttachment;
    const hasNewAttachment = !!selectedFile;
    const removedAttachment = hadAttachment && removeExistingAttachment;
    const finalAttachmentExists = hasNewAttachment || (hadAttachment && !removeExistingAttachment);

    const changesOccurred = isTextChanged || removedAttachment || hasNewAttachment;
    return changesOccurred && (trimmed.length > 0 || finalAttachmentExists);
  }, [message, editingMessageId, editingInitialText, existingAttachment, removeExistingAttachment, selectedFile]);

  const { setMessagesNotifications } = useNotificationStore(
    useShallow((state) => ({ setMessagesNotifications: state.setMessagesNotifications }))
  );

  const { selectedKey, isDrawerOpen } = useDrawerStore(
    useShallow((state) => ({ selectedKey: state.selectedKey, isDrawerOpen: state.isDrawerOpen }))
  );

  const isPipActive = useRoomActionsStore(useShallow((state) => state.pipSettings.isActive));

  const { peers } = usePeersStore();

  const {
    isMobileDevice,
    isMobileAndTabletDevice,
    participantsPeers,
    isChatEmojiButtonEnabled,
    isChatSpeechStartButtonEnabled,
    isChatFileSharingButtonEnabled,
  } = useRoomContext();

  const isPublicChat = useMemo(() => selectedReceiverData.key === EMessageType.PUBLIC, [selectedReceiverData.key]);
  const isPanelistChat = useMemo(() => selectedReceiverData.key === EMessageType.PANELIST, [selectedReceiverData.key]);

  const currentPeer = useMemo(() => {
    const socketId = SocketService.getSocketId();
    return peers.get(socketId || '');
  }, [peers]);

  const messages = useMemo(() => {
    switch (selectedReceiverData.key) {
      case EMessageType.PUBLIC:
        return publicMessages;
      case EMessageType.PANELIST:
        return panelistMessages;
      case EMessageType.PRIVATE:
      default:
        return privateMessages.get(selectedReceiverData.value) || [];
    }
  }, [selectedReceiverData.key, selectedReceiverData.value, publicMessages, privateMessages, panelistMessages]);

  const haveParticipants = useMemo(() => peers.size > 0, [peers.size]);

  const privateMessageLength = useMemo(() => Array.from(privateMessages.values()).flat().length, [privateMessages]);

  const acceptTypes = useMemo(() => allowedTypes.join(','), []);

  const isEditing = Boolean(editingMessageId);

  // build a lightweight File only when we should show the existing chip (edit compose)
  const existingFileForChip = useMemo(() => {
    if (!isEditing || !existingAttachment || removeExistingAttachment) return null;
    return new File([''], existingAttachment.fileName, {
      type: existingAttachment.fileType || '',
    });
  }, [isEditing, existingAttachment, removeExistingAttachment]);

  const handleAppendSpeechMessage = useCallback(
    (msg: string) => {
      setMessage((prev) => {
        if (prev.trim().length > 0) {
          return prev + ' ' + msg;
        }
        return msg;
      });
    },
    [setMessage]
  );

  const { startSpeech, stopSpeech } = useSpeechMessage({
    setMessage: (msg: string) => handleAppendSpeechMessage(msg),
    sendMessage: () => handleSendMessage(),
  });

  const scrollToBottom = useCallback(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, []);

  const toggleSpeech = useCallback(() => {
    if (isSpeechActive) {
      stopSpeech();
    } else {
      startSpeech();
    }
    setIsSpeechActive((prev) => !prev);
  }, [isSpeechActive, startSpeech, stopSpeech]);

  const emitTypingIndicator = useCallback(() => {
    if (isPublicChat || !selectedReceiverData) return;

    SocketService.emit(ECommonSocketKeys.USER_TYPING, {
      to_peer_id: selectedReceiverData.value,
      eventSource: currentPeer?.peer_info.peer_id,
      sourceName: currentPeer?.peer_info.peer_name,
    });
  }, [isPublicChat, selectedReceiverData, currentPeer]);

  const cleanMessage = useCallback(() => setMessage(''), []);

  const validateFile = (file: File): string | null => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

    if (blockedExtensions.includes(fileExtension)) {
      return `Files of type .${fileExtension} are not allowed.`;
    }
    if (!allowedTypes.includes(file.type) && !file.name.endsWith('.7z') && !file.name.endsWith('.rar')) {
      return 'Invalid file type. Please upload a supported file.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds the maximum limit of 25MB.';
    }
    return null;
  };

  const handleAttachFile = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsDialogOpen(false);
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setErrorMessage(error);
      return;
    }

    setErrorMessage(null);
    setSelectedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const openFileDialog = useCallback(() => {
    setIsDialogOpen(true);
    const handleCancel = () => setIsDialogOpen(false);
    if (fileInputRef) fileInputRef.current!.addEventListener('cancel', handleCancel);
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setErrorMessage(null);
  }, []);

  const handleSendMessage = useCallback(() => {
    if (isSending) return;

    const isEditing = Boolean(editingMessageId);
    const trimmedMessage = message.trim();

    /* - Helpers - */
    const finishEdit = () => {
      setEditingMessageId(null);
      setEditingInitialText('');
      setSelectedFile(null);
      setMessage('');
      setIsEmojiPickerVisible(false);
      setExistingAttachment(null);
      setRemoveExistingAttachment(false);
      setIsSending(false);
      setErrorMessage(null);
    };

    const buildToKey = (msg: IMessage) =>
      msg.type === EMessageType.PRIVATE
        ? msg.peer_id === SocketService.getSocketId()
          ? msg.to_peer_id
          : msg.peer_id
        : msg.type;

    const sendEditWithNewFile = ({
      selectedMessage,
      content,
      textChanged,
      toKey,
      timestamp,
    }: {
      selectedMessage: IMessage;
      content: string;
      textChanged: boolean;
      toKey: string | EMessageType | undefined;
      timestamp: string;
    }) => {
      //Delete old file if exists:
      if (selectedMessage.file) {
        SocketService.emit(ECommonSocketKeys.DELETE_FILE, {
          fileUrl: selectedMessage.file.fileUrl,
          type: selectedMessage.type,
        });
      }

      if (!selectedFile) return;

      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = async () => {
        setIsSending(true);

        const fileMeta = {
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          fileBuffer: reader.result as string,
        };

        // Optimistic UI with local blob URL so View/Download works immediately
        const tempUrl = URL.createObjectURL(selectedFile);
        updateMessage({
          messageId: editingMessageId!,
          to: toKey ?? '',
          updateField: {
            peer_msg: textChanged ? content : selectedMessage.peer_msg,
            edited: true,
            timestamp,
            originalTimestamp: selectedMessage.originalTimestamp || selectedMessage.timestamp,
            file: { fileName: fileMeta.fileName, fileType: fileMeta.fileType, fileUrl: tempUrl },
          },
        });

        try {
          SocketService.request(ECommonSocketKeys.FILE, {
            file: {
              fileName: selectedFile.name,
              fileType: selectedFile.type,
              fileBuffer: reader.result as string,
            },
          }).then((data) => {
            const payload = {
              ...selectedMessage,
              peer_msg: textChanged ? content : selectedMessage.peer_msg,
              timestamp,
              chatMessageId: editingMessageId,
              file: data.file, // new file
              ...(meetingType === EMeetingType.MEETING && { edited: true }),
            };
            SocketService.emit(ECommonSocketKeys.EDIT_MESSAGE, payload);
          });
        } catch (err) {
          console.error('emit editMessage failed', err);
        } finally {
          finishEdit();
        }
      };
    };

    /* - EDIT FLOW - */
    if (isEditing) {
      const selectedMessage = messages?.find((m) => m.chatMessageId === editingMessageId);
      if (!selectedMessage) {
        setEditingMessageId(null);
        setEditingInitialText('');
        return;
      }

      const originalText = (selectedMessage.peer_msg || '').trim();
      const textChanged = trimmedMessage !== originalText;
      const fileRemoved = !!existingAttachment && removeExistingAttachment;

      // Close editor if nothing changed (no emit, no "(edited)")
      if (!textChanged && !selectedFile && !fileRemoved) {
        finishEdit();
        return;
      }

      const timestamp = new Date().toISOString();
      const toKey = buildToKey(selectedMessage);

      // New file attached while editing (replaces old one if any)
      if (!!selectedFile) {
        sendEditWithNewFile({
          selectedMessage,
          content: trimmedMessage,
          textChanged,
          toKey,
          timestamp,
        });
        return;
      }

      // No new file — maybe text changed and/or file removed
      const updateField: Partial<IMessage> = {
        peer_msg: trimmedMessage,
        edited: true,
        timestamp,
        originalTimestamp: selectedMessage.originalTimestamp || selectedMessage.timestamp,
      };
      if (fileRemoved) updateField.file = null;

      updateMessage({
        messageId: editingMessageId!,
        to: toKey ?? '',
        updateField,
      });

      try {
        const payload: any = {
          ...selectedMessage,
          peer_msg: trimmedMessage,
          timestamp,
          chatMessageId: editingMessageId,
          ...(meetingType === EMeetingType.MEETING && { edited: true }),
        };
        if (fileRemoved) {
          payload.file = null;
          SocketService.emit(ECommonSocketKeys.DELETE_FILE, {
            fileUrl: selectedMessage.file.fileUrl,
            type: selectedMessage.type,
          });
        } // tell the server to drop the file

        SocketService.emit(ECommonSocketKeys.EDIT_MESSAGE, payload);
      } catch (err) {
        console.error('emit editMessage failed', err);
      } finally {
        finishEdit();
      }

      return;
    }

    /* - SEND FLOW (new message) - */

    if (!haveParticipants) {
      cleanMessage();
      return;
    }

    if (RESTRICTION_CHAT.checkForLength && message.length > RESTRICTION_CHAT.messageLength) {
      return;
    }

    const peerMsg = message.replace(/^\s+|\s+$/g, '');
    if (!peerMsg && !selectedFile?.name) {
      cleanMessage();
      return;
    }

    const me = usePeersStore.getState().peers.get(SocketService.getSocketId() || '');

    const data: IMessage = {
      room_id: getRoomId(),
      peer_id: me?.id || '',
      peer_name: me?.peer_info?.peer_name || '',
      to_peer_id: selectedReceiverData.value,
      to_peer_name: usePeersStore.getState().peers.get(selectedReceiverData.value)?.peer_info?.peer_name || '',
      peer_msg: peerMsg,
      timestamp: new Date().toISOString(),
      originalTimestamp: new Date().toISOString(),
      chatMessageId: uuidV4(),
      isShown: false,
      peer_record_id: me?.record_id || '',
      to_peer_record_id:
        selectedReceiverData.key !== EMessageType.PUBLIC
          ? usePeersStore.getState().peers.get(selectedReceiverData.value)?.record_id || ''
          : '',
      file: selectedFile ? { fileName: selectedFile.name, fileType: selectedFile.type } : null,
      type: isPublicChat ? EMessageType.PUBLIC : isPanelistChat ? EMessageType.PANELIST : EMessageType.PRIVATE,
    };

    if (selectedFile) {
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = async () => {
        setIsSending(true);
        try {
          data.file = { ...data.file, fileBuffer: reader.result as string };
          const resData = await SocketService.request(ECommonSocketKeys.FILE, data);
          // Merge returned file info into our message payload
          data.file = resData.file;
          SocketService.emit(ECommonSocketKeys.MESSAGE, data);

          appendMessage(data);
          cleanMessage();
          setSelectedFile(null);
          setIsEmojiPickerVisible(false);
          setErrorMessage(null);
        } finally {
          setIsSending(false);
        }
      };
    } else {
      SocketService.emit(ECommonSocketKeys.MESSAGE, data);
      appendMessage(data);
      cleanMessage();
      setIsEmojiPickerVisible(false);
      setErrorMessage(null);
    }
  }, [
    appendMessage,
    cleanMessage,
    editingMessageId,
    existingAttachment,
    haveParticipants,
    isPanelistChat,
    isPublicChat,
    isSending,
    message,
    messages,
    meetingType,
    removeExistingAttachment,
    selectedFile,
    selectedReceiverData.key,
    selectedReceiverData.value,
    setEditingInitialText,
    setEditingMessageId,
    updateMessage,
  ]);

  const handleDeleteMessage = useCallback(
    (chatMessageId: string) => {
      const selectedMessage = messages?.find(({ chatMessageId: messageId }) => chatMessageId === messageId);
      const myPeerId = SocketService.getSocketId();
      if (!selectedMessage) return;

      const currentPeerName =
        usePeersStore.getState().peers.get(myPeerId || '')?.peer_name || selectedMessage?.peer_name;

      SocketService.emit(ECommonSocketKeys.DELETE_MESSAGE, {
        ...selectedMessage,
        messageId: selectedMessage.chatMessageId,
        peer_id: myPeerId,
        peer_name: currentPeerName,
        to_peer_id: selectedReceiverData.value,
      });

      deleteMessage({
        messageId: chatMessageId,
        to: selectedReceiverData.key === EMessageType.PRIVATE ? selectedReceiverData.value : selectedReceiverData.key,
      });

      toast.success('Message deleted successfully', {
        position: 'top-left',
        className: 'toast toast-black',
      });
    },
    [deleteMessage, messages, selectedReceiverData.key, selectedReceiverData.value]
  );

  const handlePinMessage = useCallback(
    (chatMessageId: string) => {
      const selectedMessage = messages?.find(({ chatMessageId: messageId }) => chatMessageId === messageId);
      if (!selectedMessage) return;

      const updatedMessage = { ...selectedMessage, isPinned: !selectedMessage.isPinned };

      SocketService.emit(ECommonSocketKeys.EDIT_MESSAGE, {
        ...updatedMessage,
        peer_id: SocketService.getSocketId(),
        to_peer_id: '',
        operation: EMessageOperations.UPDATE,
        type: selectedReceiverData.key,
        operationData: { isPinned: updatedMessage.isPinned },
      });

      updateMessage({
        messageId: updatedMessage.chatMessageId || '',
        to: selectedReceiverData.key === EMessageType.PRIVATE ? selectedReceiverData.value : selectedReceiverData.key,
        updateField: { isPinned: updatedMessage.isPinned },
      });
    },
    [messages, selectedReceiverData.key, selectedReceiverData.value, updateMessage]
  );

  const handleEmojiClick = useCallback((emojiData: any) => {
    setMessage((prev) => prev + emojiData.emoji);
    setIsEmojiPickerVisible(false);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMessageId(null); // exit edit mode
    setMessage(''); // clear composer
    handleRemoveFile(); // clear any newly picked file
    setRemoveExistingAttachment(false); // keep original file as-is
    setIsEmojiPickerVisible(false);
  }, [handleRemoveFile, setEditingMessageId]);

  useEffect(() => {
    const fetchMessages = async () => {
      // Check if webinar has started before fetching chat messages
      if (meetingType !== EMeetingType.WEBINAR || !isJoinedRoom) {
        return;
      }

      const recordId = usePeersStore.getState().peers.get(selectedReceiverData.value)?.record_id || '';
      const cacheKey = selectedReceiverData.key === EMessageType.PRIVATE ? recordId : selectedReceiverData.key;

      // Check if already fetched in cache (including empty responses)
      if (chatCache.current.has(cacheKey)) return;

      // Check if messages already exist in store
      switch (selectedReceiverData.key) {
        case EMessageType.PRIVATE:
          if (privateMessages.has(selectedReceiverData.value)) return;
          break;
        case EMessageType.PUBLIC:
          if (publicMessages.length !== 0) return;
          break;
        case EMessageType.PANELIST:
          if (panelistMessages.length !== 0) return;
          break;
        default:
      }

      try {
        const response = await SocketService.request(EWebinarSockets.GET_CHAT_MESSAGES, {
          type: selectedReceiverData.key,
          ...(selectedReceiverData.key === EMessageType.PRIVATE && { to_peer_record_id: recordId }),
        });

        // Handle both successful responses and empty responses
        const messages = response?.success ? response.messages : [];

        // Update store with messages (even if empty)
        useChatStore.getState().setMessages(messages, selectedReceiverData.key, selectedReceiverData.value);

        // Cache the result (including empty responses) to prevent refetching
        const newCache = new Map(chatCache.current);
        newCache.set(cacheKey, messages);
        chatCache.current = newCache;
      } catch (error) {
        console.error('Failed to fetch chat messages:', error);
        // Cache empty array on error to prevent retry loops
        const newCache = new Map(chatCache.current);
        newCache.set(cacheKey, []);
        chatCache.current = newCache;
      }
    };

    fetchMessages();
  }, [
    panelistMessages.length,
    privateMessages,
    publicMessages.length,
    selectedReceiverData,
    isJoinedRoom,
    meetingType,
  ]);

  useEffect(() => {
    scrollToBottom();
  }, [
    privateMessageLength,
    publicMessages.length,
    panelistMessages.length,
    selectedReceiverData.key,
    selectedReceiverData.value,
    scrollToBottom,
  ]);

  useEffect(() => {
    selectedKey === 'chat' &&
      setMessagesNotifications({
        peer: selectedReceiverData.key === EMessageType.PRIVATE ? selectedReceiverData.value : selectedReceiverData.key,
        messages: [],
        isDrawerOpen,
        selectedKey,
        messageType: selectedReceiverData.key,
        isReplace: true,
        selectedReceiver: selectedReceiverData,
        isPipActive,
      });
  }, [selectedKey, isDrawerOpen, selectedReceiverData, setMessagesNotifications, isPipActive]);

  // >>> Prefill composer when entering edit mode
  useEffect(() => {
    if (!editingMessageId) return;
    setMessage(editingInitialText || '');
    requestAnimationFrame(() => {
      document.getElementById('chat-composer-textarea')?.focus();
    });
  }, [editingMessageId, editingInitialText]);

  useEffect(() => {
    if (!editingMessageId) return;

    setMessage(editingInitialText || '');

    const msg = (messages || []).find((m) => m.chatMessageId === editingMessageId);
    if (msg?.file) {
      setExistingAttachment({
        fileName: msg.file.fileName,
        fileType: msg.file.fileType,
        fileUrl: (msg.file as any).fileUrl,
      });
    } else {
      setExistingAttachment(null);
    }
    setRemoveExistingAttachment(false);

    requestAnimationFrame(() => {
      document.getElementById('chat-composer-textarea')?.focus();
    });
  }, [editingMessageId, editingInitialText, messages]);

  const handleDeleteFileAttachment = useCallback(
    (chatMessageId: string) => {
      const msg = messages?.find((m) => m.chatMessageId === chatMessageId);
      if (!msg || !msg.file) return;

      const mySocketId = SocketService.getSocketId();

      const toKey =
        selectedReceiverData.key === EMessageType.PRIVATE ? selectedReceiverData.value : selectedReceiverData.key;

      const toPeerIdForServer =
        msg.type === EMessageType.PRIVATE ? (msg.peer_id === mySocketId ? msg.to_peer_id : msg.peer_id) : '';

      const onlyFileNoText = !msg.peer_msg || msg.peer_msg.trim().length === 0;

      SocketService.emit(ECommonSocketKeys.DELETE_FILE, {
        room_id: msg.room_id,
        messageId: msg.chatMessageId,
        fileUrl: msg.file.fileUrl,
        type: msg.type,
        to_peer_id: toPeerIdForServer,
      });
      if (onlyFileNoText) {
        SocketService.emit(ECommonSocketKeys.DELETE_MESSAGE, {
          ...msg,
          messageId: msg.chatMessageId,
          peer_id: mySocketId,
          to_peer_id: toPeerIdForServer,
        });

        deleteMessage({
          messageId: chatMessageId,
          to: toKey,
        });

        toast.success('Message deleted', {
          position: 'top-left',
          className: 'toast toast-black',
        });
      } else {
        updateMessage({
          messageId: chatMessageId,
          to: toKey,
          updateField: { file: null },
        });
        SocketService.emit(ECommonSocketKeys.EDIT_MESSAGE, {
          ...msg,
          file: null,
          peer_msg: msg.peer_msg,
          chatMessageId: msg.chatMessageId,
          room_id: msg.room_id,
          type: msg.type,
          peer_id: mySocketId,
          to_peer_id: toPeerIdForServer,
          timestamp: new Date().toISOString(),
          operation: EMessageOperations.UPDATE,
        });

        toast.success('Attachment deleted', {
          position: 'top-left',
          className: 'toast toast-black',
        });
      }
    },
    [messages, selectedReceiverData.key, selectedReceiverData.value, updateMessage, deleteMessage]
  );

  return {
    theme,
    isMobileDevice,
    isMobileAndTabletDevice,

    message,
    setMessage,
    messages,
    isSpeechActive,
    isEmojiPickerVisible,
    fileInputRef,
    selectedFile,
    errorMessage,
    setErrorMessage,
    isSending,
    handleAttachFile,
    openFileDialog,
    isDialogOpen,
    handleRemoveFile,
    setIsEmojiPickerVisible,
    toggleSpeech,
    handleSendMessage,
    handleEmojiClick,
    messageContainerRef,
    handleDeleteMessage,
    selectedReceiverData,
    setSelectedReceiverData,
    publicMessages,
    privateMessages,
    participantsPeers,
    handlePinMessage,
    isPublicChat,
    emitTypingIndicator,
    typingStatus,

    isChatEmojiButtonEnabled,
    isChatFileSharingButtonEnabled,
    isChatSpeechStartButtonEnabled,

    cancelEdit,
    editingMessageId,
    setEditingMessageId,
    canSaveEdit,
    existingAttachment,
    removeExistingAttachment,
    setRemoveExistingAttachment,

    acceptTypes,
    isEditing,
    existingFileForChip,
    handleDeleteFileAttachment,
  };
};

export default useChatBox;
